import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { media } from 'src/styles/designs';
import { CleanButton } from 'src/components/common/Button';
import { DirectusImage } from 'src/components/common/DirectusImage';
import { ar, layer, lineClamp } from 'src/styles/common';
import { AnimatePresence, motion } from 'framer-motion';
import { casePathEmptyString } from 'src/utils/casePathEmptyString';
import { valueFromTargetDataset } from 'src/utils/eventHandlerHelpers';
import { always, pipe } from 'rambda';
import { makeTransition } from 'src/utils/motion/transitions';
import { useInView } from 'react-hook-inview';
import { useQuery } from 'react-query';
import { useAPIs } from 'src/hooks/useAPIs';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { ScrollToViewport } from 'src/components/common/ScrollToViewport';
import { useCallbackEffects } from 'src/hooks/useCallbackEffects';
import { Skeleton } from 'src/components/common/Skeleton';
import range from 'lodash/range';
import { dirRTL } from 'src/styles/theme';
import { NeauviaPlayerLayer } from 'src/components/player';
import { TaxonomyContext } from 'src/modules/materials/providers/TaxonomyContext';
import { ShowcaseListRenderComponentProps } from 'src/modules/materials/models/showcase';
import { GenericSectionHeader } from 'src/modules/materials/components/ShowcasePage/common';
import { VideoEntityBasic } from 'src/models/directus';
import { videoQueryByIdFactory } from 'src/modules/videos/utils/videoQueryFactory';

const Area = styled.div`
  position: relative;
  min-height: 353px;
  display: grid;
  grid-template-areas: 'number player list';
  grid-template-columns: calc(var(--results-left-margin) - 20px) 621px 1fr;
  grid-column-gap: 20px;
  overflow: hidden;
  margin: 0 calc(-1 * var(--results-right-margin) + 20px) 40px
    calc(-1 * var(--results-left-margin));

  ${dirRTL} {
    margin: 0 calc(-1 * var(--results-left-margin)) 40px
      calc(-1 * var(--results-right-margin) + 20px);
  }

  ${media.w.lessThan.desktop} {
    min-height: unset;
    grid-template-areas: 'player' 'list';
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    margin: 0 calc(-1 * var(--results-right-margin)) 42px
      calc(-1 * var(--results-left-margin));
    background: linear-gradient(
      227.25deg,
      rgba(196, 196, 196, 0) 48.08%,
      rgba(235, 235, 235, 0.47) 100%
    );

    ${dirRTL} {
      margin: 0 calc(-1 * var(--results-left-margin)) 42px
        calc(-1 * var(--results-right-margin));
    }
  }
`;

const SelectedVideoNumberContainer = styled(motion.div)`
  grid-area: number;
  margin-top: auto;
  position: relative;

  ${media.w.lessThan.desktop} {
    display: none;
  }
`;

const BigNumber = styled.div`
  font-weight: 500;
  font-size: 242.222px;
  color: ${({ theme }) => theme.palette.gray5};
  vertical-align: baseline;
  position: relative;
  top: 55px;
  left: 28px;
  ${dirRTL} {
    left: auto;
    right: 28px;
  }
`;

const ItemArea = styled.div`
  grid-area: player;
  position: relative;
  overflow: hidden;
  background: black;
  z-index: 2;

  ${ar(16 / 9)};
`;

const List = styled.div`
  grid-area: list;
  display: grid;
  grid-row-gap: 33px;
  align-items: flex-end;
  align-content: flex-start;
  margin: 19px 0;

  ${media.w.lessThan.desktop} {
    min-height: 68px;
    margin: 0;
    padding: 18px 0;

    display: flex;
    flex-direction: row;
    overflow: auto;
    gap: 0px;
  }
`;

const Index = styled.div<{ $active: boolean }>`
  font-weight: 200;
  font-size: 40px;
  line-height: 36px;
  color: ${({ theme }) => theme.text.palette.accent};
  flex: 0 1 auto;

  ${casePathEmptyString('$active')(css`
    font-weight: 500;
  `)};
`;

const Thumbnail = styled.div<{ $active: boolean }>`
  width: 67px;
  height: calc(67px * 9 / 16);
  position: relative;
  opacity: 0.3;

  img {
    object-fit: cover;
  }

  ${casePathEmptyString('$active')(css`
    opacity: 1;
  `)};

  ${media.w.lessThan.desktop} {
    display: none;
  }
`;

const Select = styled(CleanButton)`
  text-align: left;
  display: grid;
  grid-template-areas: 'number image name';
  grid-template-columns: 24px 67px 1fr;

  grid-auto-flow: row;
  grid-column-gap: 12px;
  align-items: flex-end;

  ${media.w.lessThan.desktop} {
    flex: 0 0 auto;
    display: flex;
    min-width: auto;
    flex-direction: row;

    padding: 0 calc(var(--results-right-margin)) 0
      calc(var(--results-left-margin));
  }
`;

const Name = styled.div<{ $active: boolean }>`
  ${lineClamp(2)};
  font-weight: 300;
  font-size: 12px;
  line-height: 110%;
  color: ${({ theme }) => theme.text.palette.secondary};

  ${casePathEmptyString('$active')(css`
    font-weight: 500;
    color: ${({ theme }) => theme.text.palette.accent};
  `)};
  flex: 0 1 auto;

  ${media.w.lessThan.desktop} {
    margin-bottom: 0.15em;
    max-width: 162px;
  }
`;

const Layer = styled.div`
  ${layer}
`;

export const VideoHeroListSectionRender: FC<
  ShowcaseListRenderComponentProps<VideoEntityBasic>
> = ({ section, items, isLoading }) => {
  const [selectedVideo, setSelectedVideo] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const translateItem = useTranslateItem();
  const selectVideo = useCallbackEffects(
    valueFromTargetDataset('index')(
      pipe((v: string | undefined) => (v ? parseInt(v) : 0), setSelectedVideo),
    ),
    pipe(always(true), setInteracted),
  );
  const [ref, inView] = useInView();
  const selectedVideoData = items?.[selectedVideo];
  const fullVideoData = useQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    ...videoQueryByIdFactory(useAPIs(), selectedVideoData?.id!),
    enabled: inView && !!selectedVideoData?.id,
  });

  const hasVideos = isLoading || (items != null && items.length > 0);

  const isDesktop = useMediaQuery(media.w.greaterThanOrEqual.desktop);
  const videoTransition = isDesktop
    ? makeTransition({
        from: 'rightFull',
        to: 'leftFull',
        fade: 'exit',
        speed: 'slow',
        easing: 'default',
        sortZIndex: true,
      })
    : makeTransition({
        fade: true,
        speed: 'slow',
        easing: 'default',
        sortZIndex: true,
      });

  return (
    <TaxonomyContext.Provider value={section.taxonomy}>
      <GenericSectionHeader section={section} />
      <Area>
        {hasVideos && (
          <>
            <AnimatePresence exitBeforeEnter initial={false}>
              <SelectedVideoNumberContainer
                key={selectedVideo}
                {...makeTransition({
                  from: 'rightFull',
                  to: 'leftFull',
                  fade: true,
                  easing: 'default',
                })}
              >
                <BigNumber>{selectedVideo + 1}</BigNumber>
              </SelectedVideoNumberContainer>
            </AnimatePresence>
            <ItemArea ref={ref}>
              <AnimatePresence initial={false}>
                {isLoading ? (
                  <Skeleton key={selectedVideoData?.id} fill />
                ) : (
                  selectedVideoData && (
                    <Layer key={selectedVideoData.id} {...videoTransition}>
                      <NeauviaPlayerLayer
                        video={fullVideoData.data ?? selectedVideoData}
                        autoPlay={interacted}
                      />
                    </Layer>
                  )
                )}
              </AnimatePresence>
            </ItemArea>
            <List>
              {isLoading && isDesktop
                ? range(5).map((num) => <Skeleton key={num} height={36} />)
                : items?.map(translateItem).map((video, index) => {
                    return (
                      <ScrollToViewport
                        key={video.id}
                        active={selectedVideo === index}
                      >
                        <Select data-index={index} onClick={selectVideo}>
                          <Index $active={selectedVideo === index}>
                            {index + 1}.
                          </Index>
                          <Thumbnail $active={selectedVideo === index}>
                            <DirectusImage
                              src={video.thumbnail.id}
                              layout="fill"
                            />
                          </Thumbnail>
                          <Name $active={selectedVideo === index}>
                            {video.name}
                          </Name>
                        </Select>
                      </ScrollToViewport>
                    );
                  })}
            </List>
          </>
        )}
      </Area>
    </TaxonomyContext.Provider>
  );
};
